import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from '../cx'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  /** Shows a spinner and blocks the click without collapsing the layout. */
  readonly busy?: boolean
  /** Accessible name of the spinner while `busy`, so the state is announced and not just drawn. */
  readonly busyLabel?: string
  readonly block?: boolean
  /**
   * Lets the label break over two lines instead of running past the edge. For the rare button
   * whose text comes from outside — the name of a trust service provider, say — where nobody can
   * promise it fits on a 390px screen.
   */
  readonly wrap?: boolean
  readonly children?: ReactNode
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800',
  secondary: 'border border-line bg-surface text-ink hover:bg-canvas active:bg-brand-50',
  ghost: 'bg-transparent text-brand-700 hover:bg-brand-50 active:bg-brand-100',
  danger: 'bg-danger text-white hover:brightness-95 active:brightness-90',
}

const SIZES: Record<ButtonSize, string> = {
  md: 'min-h-touch px-4 text-body',
  lg: 'min-h-14 px-5 text-lead',
}

/**
 * The one button of the design system. Both sizes clear 44px, because every action in the sign
 * flow is tapped with one thumb while the other hand holds a clipboard.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  busy = false,
  busyLabel,
  block = false,
  wrap = false,
  disabled,
  type = 'button',
  children,
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-card font-medium transition-colors',
        wrap ? 'whitespace-normal py-3 text-center' : 'whitespace-nowrap',
        'focus-visible:outline-2 focus-visible:outline-brand-600 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        block ? 'w-full' : '',
        className,
      )}
    >
      {busy ? <Spinner label={busyLabel ?? ''} size="sm" /> : null}
      {children}
    </button>
  )
}
